import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  MenuItem,
  Grid,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  UploadFile,
  Refresh,
  Info,
  ArrowBack,
  Person,
  ContactMail,
  Description,
  AccountBalance,
  DateRange,
  Home,
  LocationOn,
  MonetizationOn,
} from "@mui/icons-material";
import { propertyAPI } from "../../../services/api/property";
import { tenantAPI } from "../../../services/api/tenant";
import { artisanAPI } from "../../../services/api/artisan";
import { regionAPI } from "../../../services/api/region";
import { mergeAndSave } from "../../../utils/storageUtils";

const PropertyTab = ({ initialSearch = "" }) => {
  const [storedProperties, setStoredProperties] = useState(() => {
    const saved = localStorage.getItem("properties");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [displayProperties, setDisplayProperties] = useState([]);
  const [tenants, setTenants] = useState(() => {
    const saved = localStorage.getItem("tenants");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [artisans, setArtisans] = useState(() => {
    const saved = localStorage.getItem("artisans");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [regions, setRegions] = useState(() => {
    const saved = localStorage.getItem("regions");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false); // Can be used for loading spinner
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(initialSearch);

  const [viewMode, setViewMode] = useState("buildings"); // 'buildings' or 'properties'
  const [allProperties, setAllProperties] = useState([]);

  const [expandedPropertyIds, setExpandedPropertyIds] = useState(new Set());

  const statusLabels = {
    vacant: "Leer",
    rented: "Vermietet",
    maintenance: "Instandhaltung",
    occupied_owner: "Eigennutzung",
    terminated_on: "Gekündigt zum:",
    in_progress: "In Bearbeitung",
    new_open: "Neu / Offen",
    renovation_in_progress: "Renovierung läuft",
    looking_for_tenant: "Nachmieter gesucht",
    completed: "Abgeschlossen",
    renovation_planned: "Renovierung geplant",
  };

  const propertyTypeLabels = {
    apartment: "Wohnung",
    house: "Haus",
    commercial: "Gewerbe",
    retail: "Einzelhandel",
    gastronomy: "Gastronomie",
    office: "Büro",
    warehouse: "Lager",
    garage: "Garage",
    land: "Land",
    other: "Andere",
  };

  const positionLabels = {
    left: "Links",
    right: "Rechts",
    center: "Mitte",
    center_right: "Mitte Rechts",
    center_left: "Mitte Links",
    front: "Vorne",
    rear: "Hinten",
    entire_floor: "Ganzer Stock",
    ground_floor: "Erdgeschoss",
    upper_floor: "Obergeschoss",
    penthouse: "Penthouse",
    basement: "Keller",
    duplex: "Duplex",
  };

  const getStatusLabel = (status) => statusLabels[status] || status;

  const getPositionLabel = (position) => positionLabels[position] || position;

  useEffect(() => {
    if (initialSearch) {
      // If the search looks like a numeric id, switch to the 'properties' (Einheiten) view
      const trimmed = String(initialSearch).trim();
      const asNumber = Number(trimmed);
      if (trimmed !== "" && !Number.isNaN(asNumber)) {
        setViewMode("properties");
        // Prefix numeric searches with `id=` so the search bar shows `id=3` etc.
        setSearch(`id=${trimmed}`);
      } else {
        // keep buildings view for non-numeric searches
        setViewMode("buildings");
        setSearch(initialSearch);
      }
    }
  }, [initialSearch]);

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [openEditBuildingDialog, setOpenEditBuildingDialog] = useState(false);
  const [openDeleteBuildingDialog, setOpenDeleteBuildingDialog] =
    useState(false);
  const [currentBuilding, setCurrentBuilding] = useState(null);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [expandedPropertyId, setExpandedPropertyId] = useState(null);
  const [expandedBuildingId, setExpandedBuildingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingBuilding, setSavingBuilding] = useState(false);

  // Initial State for form
  const initialFormState = {
    property_number: "",
    address: "",
    city: "",
    state: "Saarland",
    zip_code: "",
    price: "",
    square_meters: "",
    property_type: "apartment",
    description: "",
    floor: "",
    position: "",
    status: "rented",
    terminated_on: "",
    move_out_date: "",
    construction_year: "",
    tenant_ids: [],
    new_tenant_ids: [],
    artisan_ids: [],
    region_id: null,
  };
  const [formData, setFormData] = useState(initialFormState);

  const initialBuildingFormState = {
    address: "",
    city: "",
    state: "",
    zip_code: "",
    region_id: null,
  };
  const [buildingFormData, setBuildingFormData] = useState(
    initialBuildingFormState,
  );

  const fetchProperties = async (fetchAll = false) => {
    setLoading(true);
    try {
      const limit = fetchAll ? 10000 : rowsPerPage; // Large limit for all properties
      const skip = fetchAll ? 0 : page * rowsPerPage;
      const response = await propertyAPI.getProperties({
        skip,
        limit,
        search: fetchAll ? "" : search, // For all, no search
      });
      if (fetchAll) {
        setAllProperties(response.data);
      } else {
        setDisplayProperties(response.data);
        setStoredProperties((prev) => {
          const merged = mergeAndSave("properties", response.data);
          return merged;
        });
      }
      setError(null);
    } catch (err) {
      console.error("Fehler beim Abrufen der Immobilien", err);
      setError("Fehler beim Abrufen der Immobilien");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === "buildings") {
      fetchProperties(true); // Fetch all for buildings
    } else {
      fetchProperties(false); // Fetch paginated for properties
    }
  }, [page, rowsPerPage, search, viewMode]);

  const fetchTenants = async () => {
    try {
      const response = await tenantAPI.getTenants({ limit: 1000 }); // Get all
      setTenants(response.data);
      mergeAndSave("tenants", response.data);
    } catch (err) {
      console.error("Fehler beim Laden der Mieter", err);
    }
  };

  const fetchArtisans = async () => {
    try {
      const response = await artisanAPI.getArtisans({ limit: 1000 }); // Get all
      setArtisans(response.data);
      mergeAndSave("artisans", response.data);
    } catch (err) {
      console.error("Fehler beim Laden der Handwerker", err);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await regionAPI.getRegions({ limit: 1000 }); // Get all
      setRegions(response.data);
      mergeAndSave("regions", response.data);
    } catch (err) {
      console.error("Fehler beim Laden der Regionen", err);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchArtisans();
    fetchRegions();
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleViewChange = (event, newValue) => {
    setViewMode(newValue);
    setSearch("");
    setPage(0);
  };

  const getBuildings = () => {
    const addressMap = {};
    allProperties.forEach((property) => {
      const addr = property.address;
      if (addr) {
        if (!addressMap[addr]) {
          addressMap[addr] = {
            count: 0,
            city: property.city,
            zip: property.zip_code,
            state: property.state,
            region_id: property.region_id,
          };
        }
        addressMap[addr].count += 1;
      }
    });
    return Object.entries(addressMap).map(([address, data]) => ({
      address,
      count: data.count,
      city: data.city,
      zip: data.zip,
      state: data.state,
      region_id: data.region_id,
    }));
  };

  const handleAddPropertyToBuilding = (building) => {
    const propsInBuilding = allProperties.filter(
      (p) => p.address === building.address,
    );
    const commonState =
      propsInBuilding.length > 0 ? propsInBuilding[0].state : "Saarland";
    const commonConstructionYear =
      propsInBuilding.length > 0 ? propsInBuilding[0].construction_year : "";
    const allArtisans = new Set();
    propsInBuilding.forEach((p) => {
      if (p.artisans) p.artisans.forEach((a) => allArtisans.add(a.id));
    });
    const artisanIds = Array.from(allArtisans);
    setFormData({
      ...initialFormState,
      address: building.address,
      city: building.city,
      zip_code: building.zip,
      state: commonState,
      construction_year: commonConstructionYear,
      artisan_ids: artisanIds,
    });
    setOpenDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (property = null) => {
    // Reload tenants and artisans to ensure we have all available suggestions
    await Promise.all([fetchTenants(), fetchArtisans(), fetchRegions()]);

    if (property) {
      setCurrentProperty(property);
      setFormData({
        property_number: property.property_number,
        address: property.address,
        city: property.city,
        state: property.state,
        zip_code: property.zip_code,
        price: property.price,
        square_meters: property.square_meters || "",
        property_type: property.property_type || "apartment",
        description: property.description || "",
        floor: property.floor || "",
        position: property.position || "",
        status: property.status || "rented",
        terminated_on: property.terminated_on || "",
        move_out_date: property.move_out_date || "",
        construction_year: property.construction_year || "",
        tenant_ids: property.tenants ? property.tenants.map((t) => t.id) : [],
        new_tenant_ids: property.new_tenants
          ? property.new_tenants.map((t) => t.id)
          : [],
        artisan_ids: property.artisans
          ? property.artisans.map((a) => a.id)
          : [],
        region_id: property.region_id || null,
      });
    } else {
      setCurrentProperty(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  useEffect(() => {
    if (openDialog) {
      setSaving(false);
    }
  }, [openDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentProperty(null);
    setFormData(initialFormState);
  };

  const handleDeleteClick = (property) => {
    setCurrentProperty(property);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await propertyAPI.deleteProperty(currentProperty.id);
      fetchProperties(viewMode === "buildings");
      setOpenDeleteDialog(false);
      setCurrentProperty(null);
    } catch (err) {
      console.error("Fehler beim Löschen der Immobilie", err);
      setError("Fehler beim Löschen der Immobilie");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "status" && value !== "terminated_on") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        terminated_on: "",
        move_out_date: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (currentProperty) {
        await propertyAPI.updateProperty(currentProperty.id, formData);
      } else {
        await propertyAPI.createProperty(formData);
      }
      fetchProperties(viewMode === "buildings");
      handleCloseDialog();
    } catch (err) {
      console.error("Fehler beim Speichern der Immobilie", err);
      setError(
        "Fehler beim Speichern der Immobilie. Stellen Sie sicher, dass die Immobiliennummer eindeutig ist.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await propertyAPI.importPropertiesFromXlsx(file);
      fetchProperties(viewMode === "buildings");
    } catch (err) {
      console.error("Fehler beim Importieren der Immobilien", err);
      setError("Fehler beim Importieren der Immobilien");
    }
  };

  const handleEditBuilding = (building) => {
    setCurrentBuilding(building);
    setBuildingFormData({
      address: building.address,
      city: building.city,
      state: building.state,
      zip_code: building.zip,
      region_id: building.region_id || null,
    });
    setOpenEditBuildingDialog(true);
  };

  useEffect(() => {
    if (openEditBuildingDialog) {
      setSavingBuilding(false);
    }
  }, [openEditBuildingDialog]);

  const handleDeleteBuilding = (building) => {
    setCurrentBuilding(building);
    setOpenDeleteBuildingDialog(true);
  };

  const handleBuildingFormChange = (e) => {
    const { name, value } = e.target;
    setBuildingFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBuildingAutocompleteChange = (name, value) => {
    setBuildingFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditBuildingSubmit = async () => {
    setSavingBuilding(true);
    try {
      // Get all properties in this building
      const propertiesToUpdate = allProperties.filter(
        (p) => p.address === currentBuilding.address,
      );

      // Update each property
      for (const property of propertiesToUpdate) {
        await propertyAPI.updateProperty(property.id, {
          address: buildingFormData.address,
          city: buildingFormData.city,
          state: buildingFormData.state,
          zip_code: buildingFormData.zip_code,
          region_id: buildingFormData.region_id,
        });
      }

      fetchProperties(true); // Refresh all properties
      setOpenEditBuildingDialog(false);
      setCurrentBuilding(null);
      setBuildingFormData(initialBuildingFormState);
    } catch (err) {
      console.error("Fehler beim Aktualisieren der Immobilie", err);
      setError("Fehler beim Aktualisieren der Immobilie");
    } finally {
      setSavingBuilding(false);
    }
  };

  const handleConfirmDeleteBuilding = async () => {
    try {
      // Get all properties in this building
      const propertiesToDelete = allProperties.filter(
        (p) => p.address === currentBuilding.address,
      );

      // Delete each property
      for (const property of propertiesToDelete) {
        await propertyAPI.deleteProperty(property.id);
      }

      fetchProperties(true); // Refresh all properties
      setOpenDeleteBuildingDialog(false);
      setCurrentBuilding(null);
    } catch (err) {
      console.error("Fehler beim Löschen der Immobilie", err);
      setError("Fehler beim Löschen der Immobilie");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Immobilien
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tabs value={viewMode} onChange={handleViewChange}>
            <Tab label="Immobilie" value="buildings" />
            <Tab label="Einheit" value="properties" />
          </Tabs>
        </Box>
        <Box>
          {viewMode === "properties" && (
            <TextField
              label="Suche"
              variant="outlined"
              size="small"
              value={search ?? ""}
              onChange={handleSearchChange}
              placeholder="Suche nach Nummer, Adresse..."
              sx={{ mr: 1 }}
            />
          )}
          <Button
            variant="outlined"
            onClick={() => fetchProperties(viewMode === "buildings")}
            startIcon={<Refresh />}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <IconButton
            onClick={() => setOpenInfoDialog(true)}
            sx={{ mr: 1 }}
            color="primary"
          >
            <Info />
          </IconButton>
          <input
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            id="raised-button-file-property"
            data-testid="raised-button-file-property"
            type="file"
            onChange={handleImport}
          />
          <label htmlFor="raised-button-file-property">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFile />}
              sx={{ mr: 1 }}
            >
              Import
            </Button>
          </label>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Einheit hinzufügen
          </Button>
        </Box>
      </Box>

      {viewMode === "buildings" ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Adresse</TableCell>
                <TableCell>Stadt</TableCell>
                <TableCell>PLZ</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Anzahl Einheiten</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getBuildings().map((building) => (
                <React.Fragment key={building.address}>
                  <TableRow
                    hover
                    onClick={() =>
                      setExpandedBuildingId(
                        expandedBuildingId === building.address
                          ? null
                          : building.address,
                      )
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{building.address}</TableCell>
                    <TableCell>{building.city}</TableCell>
                    <TableCell>{building.zip}</TableCell>
                    <TableCell>
                      {regions.find((r) => r.id === building.region_id)?.name ||
                        "-"}
                    </TableCell>
                    <TableCell>{building.count}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditBuilding(building)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteBuilding(building)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                      <IconButton
                        onClick={() => handleAddPropertyToBuilding(building)}
                      >
                        <Add />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={6}
                    >
                      <Collapse
                        in={expandedBuildingId === building.address}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Einheiten in {building.address}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Nummer</TableCell>
                                <TableCell>Typ</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Mieter</TableCell>
                                <TableCell>Preis</TableCell>
                                <TableCell>Aktionen</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allProperties
                                .filter((p) => p.address === building.address)
                                .map((property) => (
                                  <React.Fragment key={property.id}>
                                    <TableRow
                                      hover
                                      onClick={() => {
                                        setExpandedPropertyIds((prev) => {
                                          const newSet = new Set(prev);
                                          if (newSet.has(property.id)) {
                                            newSet.delete(property.id);
                                          } else {
                                            newSet.add(property.id);
                                          }
                                          return newSet;
                                        });
                                      }}
                                      sx={{ cursor: "pointer" }}
                                    >
                                      <TableCell>
                                        {property.property_number}
                                      </TableCell>
                                      <TableCell>
                                        {propertyTypeLabels[
                                          property.property_type
                                        ] || property.property_type}
                                      </TableCell>
                                      <TableCell>
                                        {getStatusLabel(property.status)}
                                        {property.status === "terminated_on" &&
                                        property.terminated_on
                                          ? ` (${property.terminated_on})`
                                          : ""}
                                        {property.status === "terminated_on" &&
                                        property.move_out_date
                                          ? ` [Auszug: ${property.move_out_date}]`
                                          : ""}
                                      </TableCell>
                                      <TableCell>
                                        {property.tenants &&
                                        property.tenants.length > 0
                                          ? property.tenants.map((t) => (
                                              <div key={t.id}>
                                                <a
                                                  href={`/asset-overview?tab=tenants&search=id=${t.id}`}
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  style={{
                                                    color: "#1976d2",
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  {t.name}
                                                </a>
                                              </div>
                                            ))
                                          : "-"}
                                      </TableCell>
                                      <TableCell>{property.price}</TableCell>
                                      <TableCell>
                                        <IconButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDialog(property);
                                          }}
                                        >
                                          <Edit />
                                        </IconButton>
                                        <IconButton
                                          color="error"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(property);
                                          }}
                                        >
                                          <Delete />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell
                                        style={{
                                          paddingBottom: 0,
                                          paddingTop: 0,
                                        }}
                                        colSpan={5}
                                      >
                                        <Collapse
                                          in={expandedPropertyIds.has(
                                            property.id,
                                          )}
                                          timeout="auto"
                                          unmountOnExit
                                        >
                                          <Box sx={{ margin: 2 }}>
                                            <Typography
                                              variant="h6"
                                              gutterBottom
                                              component="div"
                                            >
                                              Details
                                            </Typography>
                                            <Grid container spacing={2}>
                                              <Grid item xs={6}>
                                                <Typography variant="body2">
                                                  <strong>Bundesland:</strong>{" "}
                                                  {property.state}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="body2">
                                                  <strong>PLZ:</strong>{" "}
                                                  {property.zip_code}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="body2">
                                                  <strong>Größe:</strong>{" "}
                                                  {property.square_meters
                                                    ? `${property.square_meters} m²`
                                                    : "-"}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="body2">
                                                  <strong>Baujahr:</strong>{" "}
                                                  {property.construction_year ||
                                                    "-"}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="body2">
                                                  <strong>Stockwerk:</strong>{" "}
                                                  {property.floor || "-"}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="body2">
                                                  <strong>Position:</strong>{" "}
                                                  {getPositionLabel(
                                                    property.position,
                                                  ) || "-"}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={12}>
                                                <Typography variant="body2">
                                                  <strong>Beschreibung:</strong>{" "}
                                                  {property.description || "-"}
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                            {/* Show assigned artisans if present */}
                                            {Array.isArray(property.artisans) &&
                                              property.artisans.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                  <Typography
                                                    variant="subtitle2"
                                                    sx={{ mb: 1 }}
                                                  >
                                                    Zugewiesene Handwerker:
                                                  </Typography>
                                                  <ul
                                                    style={{ paddingLeft: 18 }}
                                                  >
                                                    {property.artisans.map(
                                                      (artisan) => (
                                                        <li key={artisan.id}>
                                                          <a
                                                            href={`/asset-overview?tab=artisans&search=${artisan.id}`}
                                                            style={{
                                                              color: "#1976d2",
                                                              textDecoration:
                                                                "underline",
                                                              cursor: "pointer",
                                                            }}
                                                            rel="noopener noreferrer"
                                                          >
                                                            {
                                                              artisan.company_name
                                                            }
                                                            {artisan.trade
                                                              ? ` (${artisan.trade})`
                                                              : ""}
                                                          </a>
                                                        </li>
                                                      ),
                                                    )}
                                                  </ul>
                                                </Box>
                                              )}

                                            {/* Show assigned tenants if present */}
                                            {Array.isArray(property.tenants) &&
                                              property.tenants.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                  <Typography
                                                    variant="subtitle2"
                                                    sx={{ mb: 1 }}
                                                  >
                                                    Zugewiesene Mieter:
                                                  </Typography>
                                                  <ul
                                                    style={{ paddingLeft: 18 }}
                                                  >
                                                    {property.tenants.map(
                                                      (tenant) => (
                                                        <li key={tenant.id}>
                                                          <a
                                                            href={`/asset-overview?tab=tenants&search=${tenant.id}`}
                                                            style={{
                                                              color: "#1976d2",
                                                              textDecoration:
                                                                "underline",
                                                              cursor: "pointer",
                                                            }}
                                                            rel="noopener noreferrer"
                                                          >
                                                            {tenant.name}
                                                          </a>
                                                        </li>
                                                      ),
                                                    )}
                                                  </ul>
                                                </Box>
                                              )}

                                            {/* Show former (inactive) tenants if present */}
                                            {Array.isArray(
                                              property.former_tenants,
                                            ) &&
                                              property.former_tenants.length >
                                                0 && (
                                                <Box sx={{ mt: 2 }}>
                                                  <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                      mb: 1,
                                                      color: "text.secondary",
                                                    }}
                                                  >
                                                    Ehemalige Mieter:
                                                  </Typography>
                                                  <ul
                                                    style={{ paddingLeft: 18 }}
                                                  >
                                                    {property.former_tenants.map(
                                                      (tenant) => (
                                                        <li key={tenant.id}>
                                                          <a
                                                            href={`/asset-overview?tab=tenants&search=id=${tenant.id}`}
                                                            style={{
                                                              color:
                                                                "text.secondary",
                                                              textDecoration:
                                                                "underline",
                                                              cursor: "pointer",
                                                              opacity: 0.7,
                                                            }}
                                                            rel="noopener noreferrer"
                                                          >
                                                            {tenant.name}
                                                          </a>
                                                        </li>
                                                      ),
                                                    )}
                                                  </ul>
                                                </Box>
                                              )}
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {getBuildings().length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Keine Immobilien gefunden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nummer</TableCell>
                  <TableCell>Adresse</TableCell>
                  <TableCell>Stadt</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Mieter</TableCell>
                  <TableCell>Preis</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayProperties.map((property) => (
                  <React.Fragment key={property.id}>
                    <TableRow
                      hover
                      onClick={() =>
                        setExpandedPropertyId(
                          expandedPropertyId === property.id
                            ? null
                            : property.id,
                        )
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{property.property_number}</TableCell>
                      <TableCell>{property.address}</TableCell>
                      <TableCell>{property.city}</TableCell>
                      <TableCell>
                        {propertyTypeLabels[property.property_type] ||
                          property.property_type}
                      </TableCell>
                      <TableCell>
                        {getStatusLabel(property.status)}
                        {property.status === "terminated_on" &&
                        property.terminated_on
                          ? ` (${property.terminated_on})`
                          : ""}
                        {property.status === "terminated_on" &&
                        property.move_out_date
                          ? ` [Auszug: ${property.move_out_date}]`
                          : ""}
                      </TableCell>
                      <TableCell>
                        {property.tenants && property.tenants.length > 0
                          ? property.tenants.map((t) => (
                              <div key={t.id}>
                                <a
                                  href={`/asset-overview?tab=tenants&search=id=${t.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    color: "#1976d2",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {t.name}
                                </a>
                              </div>
                            ))
                          : "-"}
                      </TableCell>
                      <TableCell>{property.price}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(property);
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(property);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={7}
                      >
                        <Collapse
                          in={expandedPropertyId === property.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 2 }}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              component="div"
                            >
                              Details
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Bundesland:</strong> {property.state}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>PLZ:</strong> {property.zip_code}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Größe:</strong>{" "}
                                  {property.square_meters
                                    ? `${property.square_meters} m²`
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Baujahr:</strong>{" "}
                                  {property.construction_year || "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Stockwerk:</strong>{" "}
                                  {property.floor || "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  <strong>Position:</strong>{" "}
                                  {getPositionLabel(property.position) || "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2">
                                  <strong>Beschreibung:</strong>{" "}
                                  {property.description || "-"}
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Show assigned artisans if present */}
                            {Array.isArray(property.artisans) &&
                              property.artisans.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1 }}
                                  >
                                    Zugewiesene Handwerker:
                                  </Typography>
                                  <ul style={{ paddingLeft: 18 }}>
                                    {property.artisans.map((artisan) => (
                                      <li key={artisan.id}>
                                        <a
                                          href={`/asset-overview?tab=artisans&search=${artisan.id}`}
                                          style={{
                                            color: "#1976d2",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                          }}
                                          rel="noopener noreferrer"
                                        >
                                          {artisan.company_name}
                                          {artisan.trade
                                            ? ` (${artisan.trade})`
                                            : ""}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </Box>
                              )}

                            {/* Show assigned tenants if present */}
                            {Array.isArray(property.tenants) &&
                              property.tenants.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1 }}
                                  >
                                    Zugewiesene Mieter:
                                  </Typography>
                                  <ul style={{ paddingLeft: 18 }}>
                                    {property.tenants.map((tenant) => (
                                      <li key={tenant.id}>
                                        <a
                                          href={`/asset-overview?tab=tenants&search=${tenant.id}`}
                                          style={{
                                            color: "#1976d2",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                          }}
                                          rel="noopener noreferrer"
                                        >
                                          {tenant.name}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </Box>
                              )}

                            {/* Show former (inactive) tenants if present */}
                            {Array.isArray(property.former_tenants) &&
                              property.former_tenants.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, color: "text.secondary" }}
                                  >
                                    Ehemalige Mieter:
                                  </Typography>
                                  <ul style={{ paddingLeft: 18 }}>
                                    {property.former_tenants.map((tenant) => (
                                      <li key={tenant.id}>
                                        <a
                                          href={`/asset-overview?tab=tenants&search=id=${tenant.id}`}
                                          style={{
                                            color: "inherit",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            opacity: 0.6,
                                          }}
                                          rel="noopener noreferrer"
                                        >
                                          {tenant.name}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </Box>
                              )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                {displayProperties.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Keine Einheiten gefunden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={-1} // Backend pagination doesn't return total count yet in generic response, simple implementation
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
            }
          />
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Home sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {currentProperty
                  ? "Einheit bearbeiten"
                  : "Neue Einheit erstellen"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {currentProperty
                  ? "Aktualisieren Sie die Einheitsdaten"
                  : "Füllen Sie die erforderlichen Felder aus, um eine neue Einheit hinzuzufügen"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={4}>
              {/* Identifikation */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e3f2fd",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Description color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Identifikation
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Immobiliennummer"
                          name="property_number"
                          value={formData.property_number ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          type="number"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Preis (€)"
                          name="price"
                          type="number"
                          value={formData.price ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          select
                          label="Status"
                          name="status"
                          value={formData.status ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <MenuItem value="vacant">Leer</MenuItem>
                          <MenuItem value="rented">Vermietet</MenuItem>
                          <MenuItem value="maintenance">
                            Instandhaltung
                          </MenuItem>
                          <MenuItem value="occupied_owner">
                            Eigennutzung
                          </MenuItem>
                          <MenuItem value="terminated_on">
                            Gekündigt zum:
                          </MenuItem>
                          <MenuItem value="in_progress">
                            In Bearbeitung
                          </MenuItem>
                          <MenuItem value="new_open">Neu / Offen</MenuItem>
                          <MenuItem value="renovation_in_progress">
                            Renovierung läuft
                          </MenuItem>
                          <MenuItem value="looking_for_tenant">
                            Nachmieter gesucht
                          </MenuItem>
                          <MenuItem value="completed">Abgeschlossen</MenuItem>
                          <MenuItem value="renovation_planned">
                            Renovierung geplant
                          </MenuItem>
                        </TextField>
                      </Grid>
                      {formData.status === "terminated_on" && (
                        <>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label="Gekündigt zum"
                              name="terminated_on"
                              type="date"
                              value={formData.terminated_on ?? ""}
                              onChange={handleFormChange}
                              variant="outlined"
                              slotProps={{ inputLabel: { shrink: true } }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label="Auszugsdatum"
                              name="move_out_date"
                              type="date"
                              value={formData.move_out_date ?? ""}
                              onChange={handleFormChange}
                              variant="outlined"
                              slotProps={{ inputLabel: { shrink: true } }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Standort */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e8f5e8",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <LocationOn color="success" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "success.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Standort
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          label="Adresse"
                          name="address"
                          value={formData.address ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          label="Postleitzahl"
                          name="zip_code"
                          value={formData.zip_code ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          slotProps={{
                            htmlInput: {
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                              maxLength: 5,
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          label="Stadt"
                          name="city"
                          value={formData.city ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          select
                          label="Bundesland"
                          name="state"
                          value={formData.state ?? ""}
                          onChange={handleFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          {[
                            "Baden-Württemberg",
                            "Bayern",
                            "Berlin",
                            "Brandenburg",
                            "Bremen",
                            "Hamburg",
                            "Hessen",
                            "Mecklenburg-Vorpommern",
                            "Niedersachsen",
                            "Nordrhein-Westfalen",
                            "Rheinland-Pfalz",
                            "Saarland",
                            "Sachsen",
                            "Sachsen-Anhalt",
                            "Schleswig-Holstein",
                            "Thüringen",
                          ].map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Region assign */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fff3e0",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <LocationOn color="warning" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "warning.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Region zuweisen
                      </Typography>
                    </Box>
                    <Autocomplete
                      options={regions}
                      getOptionLabel={(option) => option.name}
                      value={
                        regions.find((r) => r.id === formData.region_id) || null
                      }
                      onChange={(event, newValue) => {
                        handleAutocompleteChange(
                          "region_id",
                          newValue ? newValue.id : null,
                        );
                      }}
                      noOptionsText="Keine Regionen verfügbar"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Region auswählen"
                          placeholder="Region suchen..."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              minWidth: 300,
                            },
                          }}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Details & Specs */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fce4ec",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <MonetizationOn color="error" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "error.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Details & Eigenschaften
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="Fläche (m²)"
                          name="square_meters"
                          type="number"
                          value={formData.square_meters ?? ""}
                          onChange={handleFormChange}
                          onKeyDown={(e) => {
                            // Physically block the minus sign, plus sign, and scientific notation 'e'
                            if (["-", "+", "e"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          slotProps={{
                            htmlInput: {
                              min: 0,
                              step: "any", // Allows decimals (e.g., 50.5 m²) while blocking negatives
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="Baujahr"
                          name="construction_year"
                          type="number"
                          value={formData.construction_year ?? ""}
                          onChange={handleFormChange}
                          onKeyDown={(e) => {
                            // Prevents the typing of '-', '+', and 'e' (scientific notation)
                            if (["-", "+", "e", "."].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          slotProps={{
                            htmlInput: {
                              min: 0,
                              max: new Date().getFullYear(), // Dynamically sets max to 2026
                              step: 1,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          label="Stockwerk"
                          name="floor"
                          value={formData.floor ?? ""}
                          onChange={handleFormChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          select
                          label="Typ"
                          name="property_type"
                          value={formData.property_type ?? ""}
                          onChange={handleFormChange}
                        >
                          <MenuItem value="apartment">Wohnung</MenuItem>
                          <MenuItem value="house">Haus</MenuItem>
                          <MenuItem value="commercial">Gewerbe</MenuItem>
                          <MenuItem value="retail">Einzelhandel</MenuItem>
                          <MenuItem value="gastronomy">Gastronomie</MenuItem>
                          <MenuItem value="office">Büro</MenuItem>
                          <MenuItem value="warehouse">Lager</MenuItem>
                          <MenuItem value="garage">Garage</MenuItem>
                          <MenuItem value="land">Land</MenuItem>
                          <MenuItem value="other">Andere</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          select
                          label="Lage"
                          name="position"
                          value={formData.position ?? ""}
                          onChange={handleFormChange}
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value="">Keine</MenuItem>
                          <MenuItem value="left">Links</MenuItem>
                          <MenuItem value="right">Rechts</MenuItem>
                          <MenuItem value="center">Mitte</MenuItem>
                          <MenuItem value="center_right">Mitte Rechts</MenuItem>
                          <MenuItem value="center_left">Mitte Links</MenuItem>
                          <MenuItem value="front">Vorne</MenuItem>
                          <MenuItem value="rear">Hinten</MenuItem>
                          <MenuItem value="entire_floor">Ganzer Stock</MenuItem>
                          <MenuItem value="ground_floor">Erdgeschoss</MenuItem>
                          <MenuItem value="upper_floor">Obergeschoss</MenuItem>
                          <MenuItem value="penthouse">Penthouse</MenuItem>
                          <MenuItem value="basement">Keller</MenuItem>
                          <MenuItem value="duplex">Duplex</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
              {/* Tenant assign */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e3f2fd",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Person color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Mieter zuweisen
                      </Typography>
                    </Box>
                    <Autocomplete
                      multiple
                      options={tenants}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.tenant_number})`
                      }
                      value={tenants.filter((t) =>
                        formData.tenant_ids.includes(t.id),
                      )}
                      onChange={(event, newValue) => {
                        handleAutocompleteChange(
                          "tenant_ids",
                          newValue.map((t) => t.id),
                        );
                      }}
                      noOptionsText="Keine Optionen"
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={`${option.name} (${option.tenant_number})`}
                            {...getTagProps({ index })}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Mieter auswählen"
                          placeholder="Mieter suchen..."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              minWidth: 300,
                            },
                          }}
                        />
                      )}
                    />

                    <Box sx={{ mt: 3 }} />

                    <Autocomplete
                      multiple
                      options={tenants}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.tenant_number})`
                      }
                      value={tenants.filter((t) =>
                        formData.new_tenant_ids.includes(t.id),
                      )}
                      onChange={(event, newValue) => {
                        handleAutocompleteChange(
                          "new_tenant_ids",
                          newValue.map((t) => t.id),
                        );
                      }}
                      noOptionsText="Keine Optionen"
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={`${option.name} (${option.tenant_number})`}
                            {...getTagProps({ index })}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Neue / Zukünftige Mieter auswählen"
                          placeholder="Zukünftige Mieter suchen..."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              minWidth: 300,
                            },
                          }}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Artisan assign */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fff3e0",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Description color="warning" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "warning.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Handwerker zuweisen
                      </Typography>
                    </Box>
                    <Autocomplete
                      multiple
                      options={artisans}
                      getOptionLabel={(option) =>
                        `${option.company_name} (${option.trade})`
                      }
                      value={artisans.filter((a) =>
                        formData.artisan_ids.includes(a.id),
                      )}
                      onChange={(event, newValue) => {
                        handleAutocompleteChange(
                          "artisan_ids",
                          newValue.map((a) => a.id),
                        );
                      }}
                      noOptionsText="Keine Optionen"
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={`${option.company_name} (${option.trade})`}
                            {...getTagProps({ index })}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Handwerker auswählen"
                          placeholder="Handwerker suchen..."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              minWidth: 300,
                            },
                          }}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e8f5e8",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <Description color="success" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "success.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Beschreibung
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Beschreibung"
                      name="description"
                      value={formData.description ?? ""}
                      onChange={handleFormChange}
                      placeholder="Zusätzliche Informationen zur Immobilie..."
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          minWidth: 600,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
            sx={{
              background: saving
                ? "linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)"
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              "&:hover": {
                background: saving
                  ? "linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)"
                  : "linear-gradient(135deg, #5a6fd8 0%, #6b5b95 100%)",
              },
            }}
          >
            {saving ? "Speichere..." : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          Sind Sie sicher, dass Sie die Immobilie{" "}
          {currentProperty?.property_number} löschen möchten?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Building Dialog */}
      <Dialog
        open={openEditBuildingDialog}
        onClose={() => setOpenEditBuildingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
            fontSize: "1.1rem",
            p: 3,
          }}
        >
          <Home />
          Immobilie bearbeiten
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Address */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e3f2fd",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <LocationOn color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Adresse
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      required
                      label="Adresse"
                      name="address"
                      value={buildingFormData.address ?? ""}
                      onChange={handleBuildingFormChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Location Details */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #f3e5f5",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    minWidth: 600,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <LocationOn color="secondary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "secondary.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Standort Details
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          label="Postleitzahl"
                          name="zip_code"
                          value={buildingFormData.zip_code ?? ""}
                          onChange={handleBuildingFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              minWidth: 300,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          label="Stadt"
                          name="city"
                          value={buildingFormData.city ?? ""}
                          onChange={handleBuildingFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          select
                          label="Bundesland"
                          name="state"
                          value={buildingFormData.state ?? ""}
                          onChange={handleBuildingFormChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          {[
                            "Baden-Württemberg",
                            "Bayern",
                            "Berlin",
                            "Brandenburg",
                            "Bremen",
                            "Hamburg",
                            "Hessen",
                            "Mecklenburg-Vorpommern",
                            "Niedersachsen",
                            "Nordrhein-Westfalen",
                            "Rheinland-Pfalz",
                            "Saarland",
                            "Sachsen",
                            "Sachsen-Anhalt",
                            "Schleswig-Holstein",
                            "Thüringen",
                          ].map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Region */}
              <Grid item xs={12}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #fff3e0",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 1,
                      }}
                    >
                      <LocationOn color="warning" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "warning.main",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        Region
                      </Typography>
                    </Box>
                    <Autocomplete
                      options={regions}
                      getOptionLabel={(option) => option.name}
                      value={
                        regions.find(
                          (r) => r.id === buildingFormData.region_id,
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        handleBuildingAutocompleteChange(
                          "region_id",
                          newValue ? newValue.id : null,
                        );
                      }}
                      noOptionsText="Keine Regionen verfügbar"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Region auswählen"
                          placeholder="Region suchen..."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              minWidth: 300,
                            },
                          }}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setOpenEditBuildingDialog(false)}
            color="inherit"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleEditBuildingSubmit}
            variant="contained"
            size="large"
            disabled={savingBuilding}
            startIcon={savingBuilding ? <CircularProgress size={20} /> : null}
            sx={{
              background: savingBuilding
                ? "linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)"
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              "&:hover": {
                background: savingBuilding
                  ? "linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)"
                  : "linear-gradient(135deg, #5a6fd8 0%, #6b5b95 100%)",
              },
            }}
          >
            {savingBuilding ? "Speichere..." : "Speichern"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Building Confirmation Dialog */}
      <Dialog
        open={openDeleteBuildingDialog}
        onClose={() => setOpenDeleteBuildingDialog(false)}
      >
        <DialogTitle>Immobilie löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie alle Einheiten in dieser Immobilie (
            {currentBuilding?.address}) löschen möchten? Diese Aktion kann nicht
            rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteBuildingDialog(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDeleteBuilding}
            color="error"
            variant="contained"
          >
            Alle löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={openInfoDialog}
        onClose={() => setOpenInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Format Informationen</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Für den Import von Immobilien verwenden Sie bitte eine Excel-Datei
            (.xlsx) mit dem folgenden Spaltenaufbau (ab Zeile 2):
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Spalte</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Beschreibung</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Datentyp / Format</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>A (0)</TableCell>
                  <TableCell>Immobiliennummer</TableCell>
                  <TableCell>Text (Pflichtfeld)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>B (1)</TableCell>
                  <TableCell>Adresse</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>C (2)</TableCell>
                  <TableCell>PLZ</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>D (3)</TableCell>
                  <TableCell>Stadt</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>E (4)</TableCell>
                  <TableCell>Bundesland</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>F (5)</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>
                    Text (Optional), z.B. Wohnung, Laden, Büro, Garage
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>G (6)</TableCell>
                  <TableCell>Stockwerk</TableCell>
                  <TableCell>Text (Optional), z.B. EG, 1.OG, DG</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>H (7)</TableCell>
                  <TableCell>Lage</TableCell>
                  <TableCell>Text (Optional), z.B. links, Hinterhaus</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>I (8)</TableCell>
                  <TableCell>Größe (m²)</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>J (9)</TableCell>
                  <TableCell>Preis (€)</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>K (10)</TableCell>
                  <TableCell>Baujahr</TableCell>
                  <TableCell>Zahl (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>L (11)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    Text (Optional), z.B. leer, eigengenutzt, in Wartung,
                    standard: vermietet{" "}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>M (12)</TableCell>
                  <TableCell>Mieter IDs</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>N (13)</TableCell>
                  <TableCell>Übergeordnete Immobiliennummer</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>O (14)</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell>Text (Optional)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Hinweise:</Typography>
            <ul>
              <li>Die erste Zeile wird als Überschrift ignoriert.</li>
              <li>
                Für "Größe" und "Preis" verwenden Sie Zahlen wie "75.5" oder
                deutsches Format "75,5" (Kommas werden automatisch in Punkte
                umgewandelt).
              </li>
              <li>
                Für "Mieter IDs" geben Sie die IDs kommagetrennt an (z.B. "1, 2,
                3").
              </li>
              <li>
                Für "Status" verwenden Sie Werte wie "leer", "vermietet",
                "eigengenutzt", "in Wartung".
              </li>
              <li>
                Für "Übergeordnete Immobiliennummer" geben Sie die Nummer der
                übergeordneten Immobilie an, falls zutreffend.
              </li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInfoDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyTab;
